import { CoreStatus, CoreStatusExtended, DeepReadonly, ISystemAndImmiConfig, ISystemAndUniConfig } from 'types';
import { deepFreeze } from '../utils/deepFreeze.js';

import { defaultFieldMappings } from './fieldMappings.js';
import { defaultFormatTypes } from './formatTypes.js';
import { defaultMappingTypes } from './mappingTypes.js';
import { reminderNotificationsConfig } from './reminderNotifications.js';

export * from './fieldMappings.js';
export * from './formatTypes.js';
export * from './mappingTypes.js';
export * from './reminderNotifications.js';
interface EmailFrom {
  casShieldEmailFrom: string;
  applyEmailFrom: string;
}

const stages = [
  CoreStatus.PRE_DEPOSIT,
  CoreStatus.POST_DEPOSIT,
  CoreStatus.READY_FOR_CAS,
  CoreStatus.CAS_ISSUED,
  CoreStatus.VISA_ISSUED,
  CoreStatus.VISA_REFUSED,
  CoreStatus.DEFERRED,
  CoreStatus.CLOSED_LOST,
];
const moveToStages = [
  { key: CoreStatus.PRE_DEPOSIT, label: CoreStatus.PRE_DEPOSIT },
  { key: CoreStatus.POST_DEPOSIT, label: CoreStatus.POST_DEPOSIT },
  { key: CoreStatus.READY_FOR_CAS, label: CoreStatus.READY_FOR_CAS },
  { key: CoreStatus.CAS_ISSUED, label: CoreStatus.CAS_ISSUED },
  { key: CoreStatus.VISA_ISSUED, label: CoreStatus.VISA_ISSUED },
  { key: CoreStatus.VISA_REFUSED, label: CoreStatus.VISA_REFUSED },
  { key: CoreStatus.DEFERRED, label: CoreStatus.DEFERRED },
  { key: CoreStatus.CLOSED_LOST, label: CoreStatus.CLOSED_LOST },
];
const uniStages = [
  CoreStatusExtended.PRE_DEPOSIT,
  CoreStatusExtended.POST_DEPOSIT,
  CoreStatusExtended.READY_FOR_CAS,
  CoreStatusExtended.CAS_ISSUED,
  CoreStatusExtended.VISA_ISSUED,
  CoreStatusExtended.VISA_REFUSED,
  CoreStatusExtended.DEFERRED,
  CoreStatusExtended.CLOSED_LOST,
];
const uniMoveToStages = [
  { key: CoreStatusExtended.PRE_DEPOSIT, label: CoreStatusExtended.PRE_DEPOSIT },
  { key: CoreStatusExtended.POST_DEPOSIT, label: CoreStatusExtended.POST_DEPOSIT },
  { key: CoreStatusExtended.READY_FOR_CAS, label: CoreStatusExtended.READY_FOR_CAS },
  { key: CoreStatusExtended.CAS_ISSUED, label: CoreStatusExtended.CAS_ISSUED },
  { key: CoreStatusExtended.VISA_ISSUED, label: CoreStatusExtended.VISA_ISSUED },
  { key: CoreStatusExtended.VISA_REFUSED, label: CoreStatusExtended.VISA_REFUSED },
  { key: CoreStatusExtended.DEFERRED, label: CoreStatusExtended.DEFERRED },
  { key: CoreStatusExtended.CLOSED_LOST, label: CoreStatusExtended.CLOSED_LOST },
];
const config = {
  urls: {
    Partners: {},
    ImmigrationProviders: {},
  },
  roles: [],
  accessKey: '',
  enrolyAccess: '',
  documentDownloads: {
    enabled: false,
    errorMessage: '',
  },
  offerEnabled: false,
  intakeIds: [],
  defaultIntakeId: 'all',
  universityIds: [],
  partnerIds: [],
  allPartners: [],
  allImmigrationProviders: [],
  stages: [...uniStages],
  moveToStages: [...uniMoveToStages],
  dashboardTabs: {
    '[UniAdmin]': [
      {
        id: 'Pre-Deposit',
        label: 'Pre-Deposit',
        statuses: [CoreStatusExtended.PRE_DEPOSIT],
        color: '#cef6ff',
        width: 175,
        isVisaStatusVisible: false,
        isDocsCountVisible: true,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: true,
        isConditionalStatusVisible: false,
        isFundsMatVisible: false,
        isDepositDateVisible: true,
      },
      {
        id: 'Post-Deposit',
        label: 'Post-Deposit',
        statuses: [CoreStatusExtended.POST_DEPOSIT],
        color: '#eafbff',
        width: 175,
        isVisaStatusVisible: false,
        isDocsCountVisible: true,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: true,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
      {
        id: 'Ready for CAS',
        label: 'Ready for CAS',
        statuses: [CoreStatusExtended.READY_FOR_CAS],
        color: '#eafbff',
        width: 175,
        isVisaStatusVisible: false,
        isDocsCountVisible: true,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: true,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
      {
        id: 'CAS Issued',
        label: 'CAS Issued',
        statuses: [CoreStatusExtended.CAS_ISSUED],
        color: '#eafbff',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: true,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
      {
        id: 'Visa Issued',
        label: 'Visa Issued',
        statuses: [CoreStatusExtended.VISA_ISSUED],
        color: '#f0fff6',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: true,
        isFollowUpDocsCountVisible: true,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: true,
        isFundsMatVisible: false,
        isDepositDateVisible: false,
      },
      {
        id: 'Visa Refused',
        label: 'Visa Refused',
        statuses: [CoreStatusExtended.VISA_REFUSED],
        color: '#fff2eb',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: true,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: false,
        isDepositDateVisible: false,
      },
      {
        id: 'Deferred',
        label: 'Deferred',
        statuses: [CoreStatusExtended.DEFERRED],
        color: '#eaeaea',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
      {
        id: 'Closed Lost',
        label: 'Closed Lost',
        statuses: [CoreStatusExtended.CLOSED_LOST],
        color: '#d1d1d1',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
    ],
    '[Uni]': [
      {
        id: 'Pre-Deposit',
        label: 'Pre-Deposit',
        statuses: [CoreStatusExtended.PRE_DEPOSIT],
        color: '#cef6ff',
        width: 175,
        isVisaStatusVisible: false,
        isDocsCountVisible: true,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: true,
        isConditionalStatusVisible: false,
        isFundsMatVisible: false,
        isDepositDateVisible: true,
      },
      {
        id: 'Post-Deposit',
        label: 'Post-Deposit',
        statuses: [CoreStatusExtended.POST_DEPOSIT],
        color: '#eafbff',
        width: 175,
        isVisaStatusVisible: false,
        isDocsCountVisible: true,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: true,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
      {
        id: 'Ready for CAS',
        label: 'Ready for CAS',
        statuses: [CoreStatusExtended.READY_FOR_CAS],
        color: '#eafbff',
        width: 175,
        isVisaStatusVisible: false,
        isDocsCountVisible: true,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: true,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
      {
        id: 'CAS Issued',
        label: 'CAS Issued',
        statuses: [CoreStatusExtended.CAS_ISSUED],
        color: '#eafbff',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: true,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
      {
        id: 'Visa Issued',
        label: 'Visa Issued',
        statuses: [CoreStatusExtended.VISA_ISSUED],
        color: '#f0fff6',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isFollowUpDocsCountVisible: true,
        isAgentDocsCountVisible: false,
        isArrStatusVisible: true,
        isConditionalStatusVisible: true,
        isFundsMatVisible: false,
        isDepositDateVisible: false,
      },
      {
        id: 'Visa Refused',
        label: 'Visa Refused',
        statuses: [CoreStatusExtended.VISA_REFUSED],
        color: '#fff2eb',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: true,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: false,
        isDepositDateVisible: false,
      },
      {
        id: 'Deferred',
        label: 'Deferred',
        statuses: [CoreStatusExtended.DEFERRED],
        color: '#eaeaea',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
      {
        id: 'Closed Lost',
        label: 'Closed Lost',
        statuses: [CoreStatusExtended.CLOSED_LOST],
        color: '#d1d1d1',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
    ],
    '[UniSecure]': [
      {
        id: 'Pre-Deposit',
        label: 'Pre-Deposit',
        statuses: [CoreStatusExtended.PRE_DEPOSIT],
        color: '#cef6ff',
        width: 175,
        isVisaStatusVisible: false,
        isDocsCountVisible: true,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: true,
        isConditionalStatusVisible: false,
        isFundsMatVisible: false,
        isDepositDateVisible: true,
      },
      {
        id: 'Post-Deposit',
        label: 'Post-Deposit',
        statuses: [CoreStatusExtended.POST_DEPOSIT],
        color: '#eafbff',
        width: 175,
        isVisaStatusVisible: false,
        isDocsCountVisible: true,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: true,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
      {
        id: 'Ready for CAS',
        label: 'Ready for CAS',
        statuses: [CoreStatusExtended.READY_FOR_CAS],
        color: '#eafbff',
        width: 175,
        isVisaStatusVisible: false,
        isDocsCountVisible: true,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: true,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
      {
        id: 'CAS Issued',
        label: 'CAS Issued',
        statuses: [CoreStatusExtended.CAS_ISSUED],
        color: '#eafbff',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: true,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
      {
        id: 'Visa Issued',
        label: 'Visa Issued',
        statuses: [CoreStatusExtended.VISA_ISSUED],
        color: '#f0fff6',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: true,
        isFollowUpDocsCountVisible: true,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: true,
        isFundsMatVisible: false,
        isDepositDateVisible: false,
      },
      {
        id: 'Visa Refused',
        label: 'Visa Refused',
        statuses: [CoreStatusExtended.VISA_REFUSED],
        color: '#fff2eb',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: true,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: false,
        isDepositDateVisible: false,
      },
      {
        id: 'Deferred',
        label: 'Deferred',
        statuses: [CoreStatusExtended.DEFERRED],
        color: '#eaeaea',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
      {
        id: 'Closed Lost',
        label: 'Closed Lost',
        statuses: [CoreStatusExtended.CLOSED_LOST],
        color: '#d1d1d1',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
    ],
    '[UniLite]': [
      {
        id: 'Pre-Deposit',
        label: 'Pre-Deposit',
        statuses: [CoreStatusExtended.PRE_DEPOSIT],
        color: '#cef6ff',
        width: 175,
        isVisaStatusVisible: false,
        isDocsCountVisible: true,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: true,
        isConditionalStatusVisible: false,
        isFundsMatVisible: false,
        isDepositDateVisible: true,
      },
      {
        id: 'Post-Deposit',
        label: 'Post-Deposit',
        statuses: [CoreStatusExtended.POST_DEPOSIT],
        color: '#eafbff',
        width: 175,
        isVisaStatusVisible: false,
        isDocsCountVisible: true,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: true,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
      {
        id: 'Ready for CAS',
        label: 'Ready for CAS',
        statuses: [CoreStatusExtended.READY_FOR_CAS],
        color: '#eafbff',
        width: 175,
        isVisaStatusVisible: false,
        isDocsCountVisible: true,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: true,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
      {
        id: 'CAS Issued',
        label: 'CAS Issued',
        statuses: [CoreStatusExtended.CAS_ISSUED],
        color: '#eafbff',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: true,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
      {
        id: 'Visa Issued',
        label: 'Visa Issued',
        statuses: [CoreStatusExtended.VISA_ISSUED],
        color: '#f0fff6',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isFollowUpDocsCountVisible: true,
        isAgentDocsCountVisible: false,
        isArrStatusVisible: true,
        isConditionalStatusVisible: true,
        isFundsMatVisible: false,
        isDepositDateVisible: false,
      },
      {
        id: 'Visa Refused',
        label: 'Visa Refused',
        statuses: [CoreStatusExtended.VISA_REFUSED],
        color: '#fff2eb',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: true,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: false,
        isDepositDateVisible: false,
      },
      {
        id: 'Deferred',
        label: 'Deferred',
        statuses: [CoreStatusExtended.DEFERRED],
        color: '#eaeaea',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
      {
        id: 'Closed Lost',
        label: 'Closed Lost',
        statuses: [CoreStatusExtended.CLOSED_LOST],
        color: '#d1d1d1',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
    ],
    '[Partner]': [
      {
        id: 'Pre-Deposit',
        label: 'Pre-Deposit',
        statuses: [CoreStatusExtended.PRE_DEPOSIT],
        color: '#cef6ff',
        width: 175,
        isVisaStatusVisible: false,
        isDocsCountVisible: true,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: false,
        isDepositDateVisible: true,
      },
      {
        id: 'Post-Deposit',
        label: 'Post-Deposit',
        statuses: [
          CoreStatusExtended.POST_DEPOSIT,
          CoreStatusExtended.INTERVIEW,
          CoreStatusExtended.COMPLIANCE_CHECK,
          CoreStatusExtended.PATHWAY_REVIEW,
          CoreStatusExtended.AWAITING_INFO,
        ],
        color: '#eafbff',
        width: 175,
        isVisaStatusVisible: false,
        isDocsCountVisible: true,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
      {
        id: 'Ready for CAS',
        label: 'Ready for CAS',
        statuses: [CoreStatusExtended.READY_FOR_CAS],
        color: '#eafbff',
        width: 175,
        isVisaStatusVisible: false,
        isDocsCountVisible: true,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
      {
        id: 'CAS Issued',
        label: 'CAS Issued',
        statuses: [CoreStatusExtended.CAS_ISSUED],
        color: '#eafbff',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: true,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
      {
        id: 'Visa Issued',
        label: 'Visa Issued',
        statuses: [CoreStatusExtended.VISA_ISSUED],
        color: '#f0fff6',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: true,
        isFollowUpDocsCountVisible: true,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: true,
        isFundsMatVisible: false,
        isDepositDateVisible: false,
      },
      {
        id: 'Visa Refused',
        label: 'Visa Refused',
        statuses: [CoreStatusExtended.VISA_REFUSED],
        color: '#fff2eb',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: true,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: false,
        isDepositDateVisible: false,
      },
      {
        id: 'Deferred',
        label: 'Deferred',
        statuses: [CoreStatusExtended.DEFERRED],
        color: '#eaeaea',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
      {
        id: 'Closed Lost',
        label: 'Closed Lost',
        statuses: [CoreStatusExtended.CLOSED_LOST],
        color: '#d1d1d1',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
    ],
    '[ImmigrationProvider]': [
      {
        id: 'Pre-Deposit',
        label: 'Pre-Deposit',
        statuses: [CoreStatusExtended.PRE_DEPOSIT],
        color: '#cef6ff',
        width: 175,
        isVisaStatusVisible: false,
        isDocsCountVisible: true,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: false,
        isDepositDateVisible: true,
      },
      {
        id: 'Post-Deposit',
        label: 'Post-Deposit',
        statuses: [
          CoreStatusExtended.POST_DEPOSIT,
          CoreStatusExtended.INTERVIEW,
          CoreStatusExtended.COMPLIANCE_CHECK,
          CoreStatusExtended.PATHWAY_REVIEW,
          CoreStatusExtended.AWAITING_INFO,
        ],
        color: '#eafbff',
        width: 175,
        isVisaStatusVisible: false,
        isDocsCountVisible: true,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
      {
        id: 'Ready for CAS',
        label: 'Ready for CAS',
        statuses: [CoreStatusExtended.READY_FOR_CAS],
        color: '#eafbff',
        width: 175,
        isVisaStatusVisible: false,
        isDocsCountVisible: true,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
      {
        id: 'CAS Issued',
        label: 'CAS Issued',
        statuses: [CoreStatusExtended.CAS_ISSUED],
        color: '#eafbff',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: true,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
      {
        id: 'Visa Issued',
        label: 'Visa Issued',
        statuses: [CoreStatusExtended.VISA_ISSUED],
        color: '#f0fff6',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: true,
        isFollowUpDocsCountVisible: true,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: true,
        isFundsMatVisible: false,
        isDepositDateVisible: false,
      },
      {
        id: 'Visa Refused',
        label: 'Visa Refused',
        statuses: [CoreStatusExtended.VISA_REFUSED],
        color: '#fff2eb',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: true,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: false,
        isDepositDateVisible: false,
      },
      {
        id: 'Deferred',
        label: 'Deferred',
        statuses: [CoreStatusExtended.DEFERRED],
        color: '#eaeaea',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
      {
        id: 'Closed Lost',
        label: 'Closed Lost',
        statuses: [CoreStatusExtended.CLOSED_LOST],
        color: '#d1d1d1',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
    ],
    '[Agent]': [
      {
        id: 'Pre-Deposit',
        label: 'Pre-Deposit',
        statuses: [CoreStatusExtended.PRE_DEPOSIT],
        color: '#cef6ff',
        width: 175,
        isVisaStatusVisible: false,
        isDocsCountVisible: true,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: true,
        isConditionalStatusVisible: false,
        isFundsMatVisible: false,
        isDepositDateVisible: true,
      },
      {
        id: 'Post-Deposit',
        label: 'Post-Deposit',
        statuses: [CoreStatusExtended.POST_DEPOSIT],
        color: '#eafbff',
        width: 175,
        isVisaStatusVisible: false,
        isDocsCountVisible: true,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: true,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
      {
        id: 'Ready for CAS',
        label: 'Ready for CAS',
        statuses: [CoreStatusExtended.READY_FOR_CAS],
        color: '#eafbff',
        width: 175,
        isVisaStatusVisible: false,
        isDocsCountVisible: true,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: true,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
      {
        id: 'CAS Issued',
        label: 'CAS Issued',
        statuses: [CoreStatusExtended.CAS_ISSUED],
        color: '#eafbff',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: true,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
      {
        id: 'Visa Issued',
        label: 'Visa Issued',
        statuses: [CoreStatusExtended.VISA_ISSUED],
        color: '#f0fff6',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: true,
        isFollowUpDocsCountVisible: true,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: true,
        isFundsMatVisible: false,
        isDepositDateVisible: false,
      },
      {
        id: 'Visa Refused',
        label: 'Visa Refused',
        statuses: [CoreStatusExtended.VISA_REFUSED],
        color: '#fff2eb',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: true,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: false,
        isDepositDateVisible: false,
      },
      {
        id: 'Deferred',
        label: 'Deferred',
        statuses: [CoreStatusExtended.DEFERRED],
        color: '#eaeaea',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
      {
        id: 'Closed Lost',
        label: 'Closed Lost',
        statuses: [CoreStatusExtended.CLOSED_LOST],
        color: '#d1d1d1',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
    ],

    '[Admin]': [
      {
        id: 'Pre-Deposit',
        label: 'Pre-Deposit',
        statuses: [CoreStatusExtended.PRE_DEPOSIT],
        color: '#cef6ff',
        width: 155,
        isVisaStatusVisible: false,
        isDocsCountVisible: true,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: true,
        isConditionalStatusVisible: false,
        isFundsMatVisible: false,
        isDepositDateVisible: true,
      },
      {
        id: 'Post-Deposit',
        label: 'Post-Deposit',
        statuses: [CoreStatusExtended.POST_DEPOSIT],
        color: '#eafbff',
        width: 155,
        isVisaStatusVisible: false,
        isDocsCountVisible: true,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: true,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
      {
        id: 'Ready for CAS',
        label: 'Ready for CAS',
        statuses: [CoreStatusExtended.READY_FOR_CAS],
        color: '#eafbff',
        width: 175,
        isVisaStatusVisible: false,
        isDocsCountVisible: true,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: true,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
      {
        id: 'CAS Issued',
        label: 'CAS Issued',
        statuses: [CoreStatusExtended.CAS_ISSUED],
        color: '#eafbff',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: true,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
      {
        id: 'Visa Issued',
        label: 'Visa Issued',
        statuses: [CoreStatusExtended.VISA_ISSUED],
        color: '#f0fff6',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: true,
        isFollowUpDocsCountVisible: true,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: true,
        isFundsMatVisible: false,
        isDepositDateVisible: false,
      },
      {
        id: 'Visa Refused',
        label: 'Visa Refused',
        statuses: [CoreStatusExtended.VISA_REFUSED],
        color: '#fff2eb',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: true,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: false,
        isDepositDateVisible: false,
      },
      {
        id: 'Deferred',
        label: 'Deferred',
        statuses: [CoreStatusExtended.DEFERRED],
        color: '#eaeaea',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
      {
        id: 'Closed Lost',
        label: 'Closed Lost',
        statuses: [CoreStatusExtended.CLOSED_LOST],
        color: '#d1d1d1',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
      {
        isArrStatusVisible: false,
        isFundsMatVisible: true,
        color: '#cef6ff',
        isDepositDateVisible: true,
        isNotInvited: true,
        label: 'Not Triggered',
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isDocsCountVisible: true,
        width: 107,
        statuses: ['Not-Triggered'],
        isFollowUpDocsCountVisible: false,
        id: 'Not-Triggered',
        isVisaStatusVisible: false,
      },
      {
        isArrStatusVisible: false,
        isFundsMatVisible: true,
        color: '#cef6ff',
        isDepositDateVisible: true,
        isNotInvited: true,
        label: 'Waiting for Intake',
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isDocsCountVisible: true,
        width: 107,
        statuses: ['Waiting for Intake'],
        isFollowUpDocsCountVisible: false,
        id: 'Waiting for Intake',
        isVisaStatusVisible: false,
      },
    ],
    '[AgencyAdmin]': [
      {
        id: 'Pre-Deposit',
        label: 'Pre-Deposit',
        statuses: [CoreStatusExtended.PRE_DEPOSIT],
        color: '#cef6ff',
        width: 175,
        isVisaStatusVisible: false,
        isDocsCountVisible: true,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: true,
        isConditionalStatusVisible: false,
        isFundsMatVisible: false,
        isDepositDateVisible: true,
      },
      {
        id: 'Post-Deposit',
        label: 'Post-Deposit',
        statuses: [CoreStatusExtended.POST_DEPOSIT],
        color: '#eafbff',
        width: 175,
        isVisaStatusVisible: false,
        isDocsCountVisible: true,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: true,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
      {
        id: 'Ready for CAS',
        label: 'Ready for CAS',
        statuses: [CoreStatusExtended.READY_FOR_CAS],
        color: '#eafbff',
        width: 175,
        isVisaStatusVisible: false,
        isDocsCountVisible: true,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: true,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
      {
        id: 'CAS Issued',
        label: 'CAS Issued',
        statuses: [CoreStatusExtended.CAS_ISSUED],
        color: '#eafbff',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: true,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
      {
        id: 'Visa Issued',
        label: 'Visa Issued',
        statuses: [CoreStatusExtended.VISA_ISSUED],
        color: '#f0fff6',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: true,
        isFollowUpDocsCountVisible: true,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: true,
        isFundsMatVisible: false,
        isDepositDateVisible: false,
      },
      {
        id: 'Visa Refused',
        label: 'Visa Refused',
        statuses: [CoreStatusExtended.VISA_REFUSED],
        color: '#fff2eb',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: true,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: false,
        isDepositDateVisible: false,
      },
      {
        id: 'Deferred',
        label: 'Deferred',
        statuses: [CoreStatusExtended.DEFERRED],
        color: '#eaeaea',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
      {
        id: 'Closed Lost',
        label: 'Closed Lost',
        statuses: [CoreStatusExtended.CLOSED_LOST],
        color: '#d1d1d1',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
    ],
    '[Enroly]': [
      {
        id: 'Pre-Deposit',
        label: 'Pre-Deposit',
        statuses: [CoreStatusExtended.PRE_DEPOSIT],
        color: '#cef6ff',
        width: 175,
        isVisaStatusVisible: false,
        isDocsCountVisible: true,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: true,
        isConditionalStatusVisible: false,
        isFundsMatVisible: false,
        isDepositDateVisible: false,
      },
      {
        id: 'Post-Deposit',
        label: 'Post-Deposit',
        statuses: [CoreStatusExtended.POST_DEPOSIT],
        color: '#eafbff',
        width: 175,
        isVisaStatusVisible: false,
        isDocsCountVisible: true,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: true,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
      {
        id: 'Ready for CAS',
        label: 'Ready for CAS',
        statuses: [CoreStatusExtended.READY_FOR_CAS],
        color: '#eafbff',
        width: 175,
        isVisaStatusVisible: false,
        isDocsCountVisible: true,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: true,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
      {
        id: 'CAS Issued',
        label: 'CAS Issued',
        statuses: [CoreStatusExtended.CAS_ISSUED],
        color: '#eafbff',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: true,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
      {
        id: 'Visa Issued',
        label: 'Visa Issued',
        statuses: [CoreStatusExtended.VISA_ISSUED],
        color: '#f0fff6',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: true,
        isFollowUpDocsCountVisible: true,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: true,
        isFundsMatVisible: false,
        isDepositDateVisible: false,
      },
      {
        id: 'Visa Refused',
        label: 'Visa Refused',
        statuses: [CoreStatusExtended.VISA_REFUSED],
        color: '#fff2eb',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: true,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: false,
        isDepositDateVisible: false,
      },
      {
        id: 'Deferred',
        label: 'Deferred',
        statuses: [CoreStatusExtended.DEFERRED],
        color: '#eaeaea',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
      {
        id: 'Closed Lost',
        label: 'Closed Lost',
        statuses: [CoreStatusExtended.CLOSED_LOST],
        color: '#d1d1d1',
        width: 175,
        isVisaStatusVisible: true,
        isDocsCountVisible: false,
        isArrStatusVisible: false,
        isFollowUpDocsCountVisible: false,
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isFundsMatVisible: true,
        isDepositDateVisible: false,
      },
      {
        isArrStatusVisible: false,
        isFundsMatVisible: true,
        color: '#cef6ff',
        isDepositDateVisible: true,
        isNotInvited: true,
        label: 'Not Triggered',
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isDocsCountVisible: true,
        width: 107,
        statuses: ['Not-Triggered'],
        isFollowUpDocsCountVisible: false,
        id: 'Not-Triggered',
        isVisaStatusVisible: false,
      },
      {
        isArrStatusVisible: false,
        isFundsMatVisible: true,
        color: '#cef6ff',
        isDepositDateVisible: true,
        isNotInvited: true,
        label: 'Waiting for Intake',
        isAgentDocsCountVisible: false,
        isConditionalStatusVisible: false,
        isDocsCountVisible: true,
        width: 107,
        statuses: ['Waiting for Intake'],
        isFollowUpDocsCountVisible: false,
        id: 'Waiting for Intake',
        isVisaStatusVisible: false,
      },
    ],
  },
  mappingTypes: defaultMappingTypes,
  setupTypes: ['Mandatory', 'Secondary', 'Disabled'],
  formatTypes: defaultFormatTypes,
  importFieldMappings: defaultFieldMappings,
  sftpImportFieldMappings: defaultFieldMappings,
  sponsor: {
    licenceNumber: '',
    schoolName: '',
    address: '',
    city: '',
    postcode: '',
  },
  defaultUniversityId: 'STG',
  university_name: 'Staging',
  university_code: 'STG',
  teamName: 'CAS team',
  documentDownloadsActive: false,
  tuitionPostDiscount: false /* university is supplying tuition fee post discount fee */,
  campuses: [],
  faculties: [],
  departments: [],
  categories: [],
  visaStatuses: [
    'None',
    'CAS not received',
    'CAS received',
    'Visa applied',
    'Awaiting visa response',
    'UKVI request more info',
    'Visa received',
    'Visa refusal',
    'Intends to defer',
    'Intends to withdraw',
  ],
  accessToken: '',
  apiEndpointUrl: 'https://public-dev-api.cas-shield.com',
  resolveConflictsModalText:
    'This action does not update your university system. Please ensure your central records are up to date.',
  defaultQuestions: [],
  trapQuestions: [],
  reminderNotifications: reminderNotificationsConfig,
  applyEnabled: false,
  apply: {
    studentNumberEnabled: false,
  },
  partialApplyJourney: false,
  allowedAliases: [],
};
export const defaultUniConfig: DeepReadonly<ISystemAndUniConfig> = deepFreeze({ ...config });

export const defaultImmigrationProviderConfig: DeepReadonly<ISystemAndImmiConfig> = deepFreeze({
  ...config,
  moveToStages,
  stages,
});

export const defaultEmailFrom: DeepReadonly<EmailFrom> = deepFreeze({
  casShieldEmailFrom: '"CAS Shield" <contact@cas-shield.com>',
  applyEmailFrom: '"Enroly Apply" <contact@enroly.com>',
});
